/**
 * ترتیبِ فهرست‌های صفحه‌ی کشف.
 *
 * منطقِ خالص و بی‌وابستگی به React، تا هم از صفحه در بیاد هم بشه تست‌ش کرد
 * (نگاه کن به sort.test.ts). خودِ گزینه‌ها توی core/lib/prefs.ts تعریف شدن
 * چون همون‌جا ذخیره می‌شن.
 *
 * قاعده‌ی مشترکِ همه‌ی ترتیب‌ها: چیزی که عددش معلوم نیست **آخر** می‌شینه، نه
 * اول. رخدادِ بی‌تاریخ یا مکانِ بی‌مختصات نباید فقط به‌خاطرِ نداشتنِ داده بالای
 * فهرست بیفته.
 */
import type { DiscoverSort } from './prefs.ts';
import type { ItemMeta } from '../data/popularityRepo.ts';
import { isRecurring, matchesDay } from './discoverWhen.ts';
import { normalize } from './discoverSearch.ts';

/** ترتیبی که توی صفحه‌ی گزینه‌ها دیده می‌شه. */
export const SORTS: DiscoverSort[] = ['nextTime', 'added', 'distance', 'popularity', 'price'];

/**
 * ترتیب‌هایی که فقط ادمین می‌بینه.
 *
 * `reviewPriority` ترتیبِ کارِ ادمینه، نه ترتیبِ نگاه کردنِ آدم‌ها: عددش از
 * کرالر می‌آد و می‌گه کدوم رخداد ارزشِ باز کردن برای بازبینی داره (مایگریشن
 * 0052). برای کسی که ادمین نیست نه معنی داره نه داده‌ش دستشه، پس اصلاً توی
 * فهرست نمی‌آد - نه خاکستر، نه بی‌اثر.
 *
 * `flagged` کنارشه ولی جنسش فرق داره: اون حدسِ کرالره و این تصمیمِ خودِ ادمین
 * — همون نشانی که با `admin_set_flag` گذاشته شده و سربرگِ «نشان‌دار»ِ صفِ
 * بازبینی فهرستش می‌کنه. صف اون‌ها رو **جدا** از فهرست نشون می‌ده؛ این ترتیب
 * برعکس، همون فهرستِ همیشگیِ کشف رو نگه می‌داره و فقط نشان‌دارها رو می‌آره
 * سرش — تا بشه دیدشون در کنارِ چیزی که ازش جدا شدن، نه به‌جاش.
 */
export const ADMIN_SORTS: DiscoverSort[] = ['reviewPriority', 'flagged'];

/**
 * ترتیب‌هایی که فقط صاحبِ پروژه می‌بینه (`TRACE_OWNER`).
 *
 * الفبایی بر اساسِ عنوان، در هر دو جهت. برای کسی که دنبالِ یه رکوردِ **مشخص**
 * توی فهرستِ کامله این کوتاه‌ترین راهه؛ ولی برای کسی که داره «چی این هفته
 * هست؟» رو می‌پرسه، حرفِ الفبا هیچ ربطی به جوابش نداره. پس مثلِ داربستِ لاگ
 * به همون یه حساب بسته‌ست، نه به `isAdmin`: ادمینِ تازه هم قرار نیست ناگهان
 * دو تا گزینه‌ی بی‌ربط ته فهرستِ ترتیب‌ش ببینه.
 */
export const OWNER_SORTS: DiscoverSort[] = ['title', 'titleDesc'];

/**
 * فهرستِ گزینه‌ها برای این کاربر.
 *
 * آبجکت و نه دو تا آرگومانِ بولی: `sortsFor(false, true)` سرِ صدا زدن هیچی
 * نمی‌گه، `sortsFor({ owner: true })` می‌گه.
 */
export const sortsFor = (
  who: { admin?: boolean; owner?: boolean } = {},
): DiscoverSort[] => [
  ...SORTS,
  ...(who.owner ? OWNER_SORTS : []),
  ...(who.admin ? ADMIN_SORTS : []),
];

/**
 * کلیدِ ترجمه‌ی هر ترتیب.
 *
 * در اپِ موبایل این‌ها نامِ آیکون هم هستن (آیکون‌ها عمداً هم‌نام نام‌گذاری
 * شدن)، ولی خودِ آن نگاشتِ نوع‌دار جایش `apps/mobile/src/features/discover`
 * است — core نباید نامِ آیکون‌های یک پلتفرم را بشناسد. اینجا فقط کلیدِ
 * ترجمه است، که هر دو کلاینت لازمش دارند.
 */
// `as const` عمدیه: نوعِ دقیقِ هر مقدار حفظ می‌شه، و همین اجازه می‌ده اپِ
// موبایل با یک خط انتساب ثابت کنه هر کدومشون آیکونِ هم‌نام هم دارن.
export const SORT_KEY = {
  nextTime: 'sortNextTime',
  added: 'sortAdded',
  distance: 'sortDistance',
  popularity: 'sortPopular',
  price: 'sortPrice',
  title: 'sortTitle',
  titleDesc: 'sortTitleDesc',
  reviewPriority: 'sortReview',
  flagged: 'sortFlagged',
} as const satisfies Record<DiscoverSort, string>;

/**
 * کمترین چیزی که برای مرتب کردن لازمه.
 *
 * هم `EventItem` این شکل رو داره هم `Business` - همه‌ی فیلدها اختیاری‌ان چون
 * مکان نه تاریخ داره نه قیمت، و رخداد تخفیف نداره.
 */
export interface Sortable {
  id: string;
  /** عنوانِ رخداد. مکان به‌جاش `name` داره - نگاه کن به titleOf. */
  title?: string | null;
  /** نامِ مکان، همتای `title`ِ رخداد. */
  name?: string | null;
  createdAt?: string | null;
  lat?: number | null;
  lng?: number | null;
  startsAt?: string | null;
  weekdays?: number[] | null;
  /** الگوی ماهانه (مایگریشن 0057) - نگاه کن به `Timeable` توی when.ts. */
  monthdays?: number[] | null;
  monthWeeks?: number[] | null;
  isFree?: boolean;
  priceNote?: string | null;
  hasDiscount?: boolean;
}

export interface SortContext {
  /** امتیاز و زمانِ افزودنِ سرور، به‌ازای شناسه. */
  metas: Map<string, ItemMeta>;
  /** موقعیتِ کاربر. نال یعنی هنوز اجازه نداده یا در دسترس نیست. */
  origin: { lat: number; lng: number } | null;
  /**
   * اولویتِ بازبینی به‌ازای شناسه - فقط برای ادمین پر می‌شه.
   *
   * چرا این‌جا و نه رویِ خودِ ردیف: عدد توی `metadata` نشسته و فهرستِ دیسکاور
   * از کشِ رویِ گوشی می‌آد که این ستون رو نداره. کشیدنِ metadata توی کش یعنی
   * همه‌ی کاربرها یه ستونِ اضافه رو دانلود کنن برای عددی که فقط ادمین
   * می‌بینه - همون قراری که shortIds داره (نگاه کن به adminRepo).
   */
  priorities?: Map<string, number>;
  /**
   * کلیدِ گروهِ شبیه‌ها، برای وقتی که دو تا آیتم امتیازِ **یکسان** دارن.
   *
   * امتیازِ بازبینی برای هر دو طرفِ یه جفت یکیه، و مرتب‌سازیِ پایدار یعنی
   * ترتیبِ ورودی می‌مونه — پس دو تا تکراری می‌تونستن ده تا کارت با هم فاصله
   * داشته باشن. این نگاشت (نگاه کن به features/admin/cluster) اون‌ها رو
   * کنارِ هم می‌آره. فقط برای ترتیبِ ادمین پر می‌شه؛ برای بقیه‌ی ترتیب‌ها
   * نال می‌مونه و هیچ‌چی عوض نمی‌شه.
   */
  groups?: Map<string, number>;
  /**
   * زمانِ نشان‌خوردن (`flagged_at`) به‌ازای شناسه - فقط برای ادمین پر می‌شه.
   *
   * جدا از خودِ ردیف، به همون دلیلِ `priorities`: نشان یه چیزِ ادمینه و
   * کشِ آفلاینِ دیسکاور این ستون رو نداره. رشته‌ی ISO می‌آد نه عدد، چون
   * همون‌جوری که از دیتابیس در می‌آد پاس داده می‌شه (نگاه کن به
   * adminRepo.flaggedAt).
   */
  flags?: Map<string, string>;
  /**
   * زبانِ فعلی، فقط برای ترتیبِ الفبایی.
   *
   * از `getLocale()` می‌آد ولی این‌جا **پاس داده** می‌شه نه import: این فایل
   * منطقِ خالصه و خوندنِ یه حالتِ سراسری یعنی تستی که جوابش به زبانِ لحظه‌ی
   * اجرا بستگی داره. نال یعنی «هرچی موتور پیش‌فرض داره».
   */
  locale?: string | null;
  now?: Date;
}

const R_EARTH_KM = 6371;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** فاصله‌ی دو نقطه روی کره، به کیلومتر (هاورساین). */
export function distanceKm(
  a: { lat: number; lng: number }, b: { lat: number; lng: number },
): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R_EARTH_KM * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * تا چند روز جلوتر دنبالِ اجرای بعدی بگردیم.
 *
 * ۷۰ چون بدترین فاصله‌ی یه الگوی ماهانه اینه: «روزِ ۳۱ِ هر ماه» بینِ ۳۱ ژانویه
 * و ۳۱ مارس ۵۹ روز فاصله داره. هفتگی هیچ‌وقت از ۷ رد نمی‌شه.
 */
const SCAN_DAYS = 70;

/**
 * لحظه‌ی اجرای بعدی، به میلی‌ثانیه. نداشتنش یعنی `Infinity` (ته فهرست).
 *
 * سه حالت:
 *   • رخدادِ تکرارشونده (هفتگی یا ماهانه): نزدیک‌ترین روزِ اجرای بعدی، با
 *     ساعتِ خودِ رخداد. رخدادِ تکرارشونده هیچ‌وقت «گذشته» نمی‌شه، پس ترتیبش هم
 *     نباید ته فهرست بره.
 *   • رخدادِ یک‌باره‌ی آینده: خودِ `startsAt`.
 *   • رخدادی که شروع شده یا اصلاً تاریخ نداره (و هر مکانی): معلوم نیست.
 *
 * جستجوی روز به روز عمداً جایگزینِ حسابِ سرانگشتیِ قبلی شد: با سه الگوی
 * هم‌زمان (روزِ هفته، روزِ ماه، nاُمین روزِ هفته‌ی ماه) هر فرمولی یه حالتِ
 * لبه‌ای جا می‌ذاشت، و ۷۰ بار پرسیدنِ «این روز؟» هم ارزون‌تره هم قابلِ خوندن.
 */
export function nextOccurrence(item: Sortable, now: Date = new Date()): number {
  const startMs = item.startsAt ? new Date(item.startsAt).getTime() : NaN;

  if (isRecurring(item)) {
    // ساعتِ اجرا از خودِ رخداد می‌آد؛ نداشته باشه، اولِ بامداد حساب می‌شه —
    // ترتیبِ روزها که مهم‌تره همچنان درست در می‌آد.
    const at = Number.isNaN(startMs) ? null : new Date(startMs);
    const h = at?.getHours() ?? 0;
    const m = at?.getMinutes() ?? 0;

    for (let i = 0; i <= SCAN_DAYS; i++) {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, h, m, 0, 0);
      // امروز بود ولی ساعتش گذشته: بی‌خیالِ امروز.
      if (day.getTime() < now.getTime()) continue;
      if (matchesDay(item, day)) return day.getTime();
    }
  }

  if (!Number.isNaN(startMs) && startMs >= now.getTime()) return startMs;
  return Infinity;
}

/**
 * رتبه‌ی قیمت: هرچی کمتر، بالاتر.
 *
 * ترتیبِ خواسته‌شده: رایگان، بعد تخفیف‌دار، بعد نامشخص، بعد چیزی که یادداشتِ
 * قیمت داره. عمداً عددِ قیمت مبنا نیست: توی دیتابیس عددی وجود نداره،
 * `price_note` یه متنِ آزاده («۲۰ دلار درِ ورودی»، «کمکِ داوطلبانه») و
 * درآوردنِ عدد از توش حدسه.
 */
export function priceRank(item: Sortable): number {
  if (item.isFree) return 0;
  if (item.hasDiscount) return 1;
  if (item.priceNote && item.priceNote.trim()) return 3;
  return 2;
}

/**
 * چیزی که ترتیبِ الفبایی روش کار می‌کنه: رخداد `title` داره و مکان `name`.
 *
 * از همون `normalize`ِ جستجو رد می‌شه، نه خامِ خام. دلیلش هم همونه: «كافه» با
 * كافِ عربی و «کافه» با کافِ فارسی دو رشته‌ی متفاوتن و بدونِ یکدست کردن، دو
 * جای کاملاً دور از هم توی فهرست می‌شینن. حروفِ بزرگ و کوچک هم همین‌طور.
 *
 * رشته‌ی خالی یعنی «عنوانی نیست» و جایگاهش ته فهرسته - نگاه کن به byTitle.
 */
export function titleOf(item: Sortable): string {
  return normalize(item.title ?? item.name ?? '');
}

/** کلیدِ مرتب‌سازی؛ کمتر یعنی بالاتر. `Infinity` یعنی «معلوم نیست، ته فهرست». */
function keyOf(item: Sortable, mode: DiscoverSort, ctx: SortContext): number {
  const meta = ctx.metas.get(item.id);

  switch (mode) {
    case 'added': {
      // ترجیح با عددِ سرور: کشِ رویِ گوشی برای رکوردهای قدیمی createdAt نداره.
      const iso = meta?.createdAt ?? item.createdAt;
      const ms = iso ? new Date(iso).getTime() : NaN;
      // تازه‌ترین اول، پس منفی.
      return Number.isNaN(ms) ? Infinity : -ms;
    }
    case 'distance': {
      if (!ctx.origin || item.lat == null || item.lng == null) return Infinity;
      return distanceKm(ctx.origin, { lat: item.lat, lng: item.lng });
    }
    case 'popularity':
      // بی‌امتیاز یعنی صفر، نه «معلوم نیست»: هیچ‌کس ندیدتش، پس ته فهرستِ
      // محبوب‌ها جای درستشه - ولی هم‌رتبه با بقیه‌ی صفرها، نه بعدِ همه.
      return -(meta?.score ?? 0);
    case 'price':
      return priceRank(item);
    case 'reviewPriority': {
      // همون `order by (metadata->>'review_priority')::int desc nulls last`:
      // بزرگ‌تر بالاتر (پس منفی)، و **نبودِ** عدد ته فهرست - نه صفر. رخدادی که
      // این کلید رو نداره یعنی هیچ جفتِ مشکوکی نداره، پس اصلاً کاری باهاش
      // نیست؛ صفر حساب کردنش قاطیِ چیزهایی می‌کردش که واقعاً امتیازشون کمه.
      const p = ctx.priorities?.get(item.id);
      return p == null ? Infinity : -p;
    }
    case 'flagged': {
      // نشان‌دارها اول و تازه‌ترین نشان بالاتر (پس منفی)؛ چیزی که نشان
      // نخورده ته فهرست می‌مونه - همون قاعده‌ی مشترکِ بالای فایل. صفر حساب
      // کردنش یعنی «همه یه‌جور»، و اون‌وقت ترتیب اصلاً تکون نمی‌خورد.
      const at = ctx.flags?.get(item.id);
      const ms = at ? new Date(at).getTime() : NaN;
      return Number.isNaN(ms) ? Infinity : -ms;
    }
    case 'nextTime':
    default:
      return nextOccurrence(item, ctx.now);
  }
}

/**
 * مسیرِ جدای ترتیبِ الفبایی — تنها ترتیبی که کلیدش رشته‌ست، نه عدد.
 *
 * قاطیِ `keyOf` نشد چون اون‌جا هر چیزی یه عدده و «معلوم نیست» با `Infinity`
 * گفته می‌شه؛ فشار دادنِ یه رشته توی اون قالب یعنی یا ساختنِ یه عددِ ساختگی
 * از حروف (که برای هر زبانی جز انگلیسی غلط در می‌آد) یا دو نوعیِ کلید توی
 * کلِ مسیر.
 */
function byTitle<T extends Sortable>(
  items: T[], mode: DiscoverSort, ctx: SortContext,
): T[] {
  const keys = new Map<string, string>();
  for (const it of items) keys.set(it.id, titleOf(it));
  const dir = mode === 'titleDesc' ? -1 : 1;
  return [...items].sort((a, b) => {
    const ka = keys.get(a.id) ?? '';
    const kb = keys.get(b.id) ?? '';
    // بی‌عنوان ته فهرست، توی **هر دو** جهت — همون قاعده‌ی مشترکِ بالای فایل.
    // برعکس کردنِ جهت نباید چیزی که اصلاً داده نداره رو بیاره صدرِ فهرست.
    if (!ka || !kb) return ka === kb ? 0 : (ka ? -1 : 1);
    if (ka === kb) return 0;
    // مقایسه‌ی زبانی، نه مقایسه‌ی کدِ نویسه: «ب» قبلِ «پ» می‌آد و «Z» قبلِ
    // «a» نمی‌آد. موتوری که Intl نداشته باشه آرگومانِ زبان رو نادیده می‌گیره
    // و به مقایسه‌ی کد برمی‌گرده — بدتر از این نمی‌شه، خطا هم نمی‌ده.
    return dir * ka.localeCompare(kb, ctx.locale ?? undefined);
  });
}

/**
 * نسخه‌ی مرتب‌شده‌ی فهرست. خودِ آرایه دست نمی‌خوره.
 *
 * ترتیبِ ورودی برای هم‌کلیدها حفظ می‌شه (مرتب‌سازیِ جاوااسکریپت پایداره)،
 * یعنی ترتیبِ سرور - «پیشنهادی»ها اول - همچنان به‌عنوانِ تصمیمِ دوم می‌مونه.
 */
export function sortItems<T extends Sortable>(
  items: T[], mode: DiscoverSort, ctx: SortContext,
): T[] {
  // ترتیبِ الفبایی تنها ترتیبیه که کلیدش عدد نیست، پس مسیرِ خودش رو داره.
  if (mode === 'title' || mode === 'titleDesc') return byTitle(items, mode, ctx);

  const keys = new Map<string, number>();
  for (const it of items) keys.set(it.id, keyOf(it, mode, ctx));
  return [...items].sort((a, b) => {
    const ka = keys.get(a.id) ?? 0;
    const kb = keys.get(b.id) ?? 0;
    // تفریق نه: دو تا `Infinity` (هر دو «معلوم نیست») NaN می‌داد و مقایسه‌گرِ
    // NaN یعنی ترتیبِ دلخواهِ موتور — همون فهرستی که هر بار جور دیگه‌ای می‌شد.
    if (ka !== kb) return ka < kb ? -1 : 1;
    // هم‌امتیاز: اگه گروهی داده شده، شبیه‌ها کنارِ هم. وگرنه صفر، یعنی همون
    // ترتیبِ ورودی — رفتارِ همیشگی.
    const ga = ctx.groups?.get(a.id);
    const gb = ctx.groups?.get(b.id);
    if (ga === undefined || gb === undefined || ga === gb) return 0;
    return ga < gb ? -1 : 1;
  });
}
